<script setup>
import { ref, useTemplateRef } from 'vue'

import ElementProgressApiHandler from '@/api/element-progress'
import {
  NUMBER_INPUT_DIGITS,
  NUMBER_INPUT_FRACTIONS
} from '@/utils/components-configurations/app-input-number'
import { REQUEST_ENTITY_KEYS } from '@/utils/entity-keys'
import { useMetricValues } from '@/utils/okr-element-composables'
import { METRIC_VALUES_KEYS, THRESHOLD_CONDITIONS } from '@/utils/okr-element-values'
import {
  includesValue,
  mapValuesByKey,
  numberOrNullProp,
  objectOrNullProp
} from '@/utils/prop-validators'

import AppDroplist from '@/components/AppDroplist'
import { INLINE_THRESHOLD_UPDATE_INPUT_TEST_ID } from '@/components/check-ins/jest-helpers'
import FormFieldNext from '@/components/form/FormFieldNext'
import {
  OKR_ELEMENT_MODAL_FOOTER_CANCEL_BUTTON_TEST_ID,
  OKR_ELEMENT_MODAL_FOOTER_CONFIRM_BUTTON_TEST_ID
} from '@/components/objectives/forms/jest-helpers'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDivider from '@/components/ui/AppDivider'
import AppInputNumberNext from '@/components/ui/AppInputNumberNext/AppInputNumberNext'

defineOptions({
  name: 'InlineThresholdUpdater',
  inheritAttrs: false
})

const props = defineProps({
  elementId: {
    type: [String, Number],
    required: true
  },

  currentThreshold: {
    required: true,
    validator: v => numberOrNullProp(v)
  },

  thresholdConditionTypeId: {
    type: Number,
    required: true,
    validator: v => includesValue(v, mapValuesByKey(THRESHOLD_CONDITIONS, 'value'))
  },

  periodEndDate: {
    type: [String, Date, Number],
    default: null
  },

  periodStartDate: {
    type: [String, Date, Number],
    default: null
  },

  appendTo: {
    type: String,
    default: '#app'
  },

  toSelector: {
    type: String,
    default: null
  },

  viewConfig: {
    validator: v => objectOrNullProp(v),
    default: null
  }
})

const isOpen = defineModel('show', {
  type: Boolean,
  set: value => value
})

const localValue = ref(props.currentThreshold)

const { getThresholdPrefix } = useMetricValues({
  element: () => {
    return {
      [METRIC_VALUES_KEYS.THRESHOLD_CONDITION_TYPE_ID]: props.thresholdConditionTypeId
    }
  }
})

const inputReference = useTemplateRef('threshold-input')

const onOpened = () => {
  inputReference.value?.focus()
  inputReference.value?.selectAll()
}

const onCancel = () => {
  isOpen.value = false
}

const isLoading = ref(false)

const emit = defineEmits({
  saved: null,
  'error-on-save': null,
  'update:is-loading': null
})

const save = async () => {
  if (props.currentThreshold === localValue.value) {
    onCancel()
    return
  }

  isLoading.value = true

  emit('update:is-loading', isLoading.value)

  const api = new ElementProgressApiHandler()

  try {
    const result = await api.updateThresholdForPeriod({
      elementId: props.elementId,
      targetValue: localValue.value,
      startDate: props.periodStartDate,
      endDate: props.periodEndDate,
      [REQUEST_ENTITY_KEYS.VIEW_CONFIG]: props.viewConfig
    })

    onCancel()
    emit('saved', result)
  } catch (error) {
    emit('error-on-save', error)
  } finally {
    isLoading.value = false
    emit('update:is-loading', isLoading.value)
  }
}
</script>

<template>
  <AppDroplist
    :append-to="appendTo"
    :model-value="isOpen"
    :offset="[0, 8]"
    :to-selector="toSelector"
    dropdown-width="300px"
    position="bottom"
    show-on-init
    trigger="manual"
    @hidden="onCancel"
    @opened="onOpened"
  >
    <div class="itu-Wrapper">
      <FormFieldNext :label="$t('objectives.threshold')">
        <AppInputNumberNext
          ref="threshold-input"
          v-model="localValue"
          :data-auto-testid="INLINE_THRESHOLD_UPDATE_INPUT_TEST_ID"
          :data-testid="INLINE_THRESHOLD_UPDATE_INPUT_TEST_ID"
          :digit-max-length="NUMBER_INPUT_DIGITS.ELEMENT_VALUES"
          :fraction="NUMBER_INPUT_FRACTIONS.ELEMENT_VALUES"
          :prefix="getThresholdPrefix"
          allow-negative
          class="itu-Input"
          full-width
          show-previous-value
          @keydown.enter.stop="save"
        />
      </FormFieldNext>
      <AppDivider />

      <div class="itu-Actions">
        <AppButton
          :data-auto-testid="OKR_ELEMENT_MODAL_FOOTER_CANCEL_BUTTON_TEST_ID"
          :data-testid="OKR_ELEMENT_MODAL_FOOTER_CANCEL_BUTTON_TEST_ID"
          type="ghost-next"
          @click="onCancel"
        >
          {{ $t('action.cancel') }}
        </AppButton>

        <AppButton
          :data-auto-testid="OKR_ELEMENT_MODAL_FOOTER_CONFIRM_BUTTON_TEST_ID"
          :data-testid="OKR_ELEMENT_MODAL_FOOTER_CONFIRM_BUTTON_TEST_ID"
          :loading="isLoading"
          type="primary-next"
          @click="save"
        >
          {{ $t('action.confirm') }}
        </AppButton>
      </div>
    </div>
  </AppDroplist>
</template>

<style lang="scss" scoped>
.itu-Wrapper {
  padding: 18px 22px;
  --divider-offset: 14px 0;
}

.itu-Input {
  --font-size: #{$fs-14};
}

.itu-Actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
